import { Injectable, NgZone } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, fromEvent, merge } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Ob das Backend gerade erreichbar ist – und zwar tatsächlich, nicht nur
 * laut Browser.
 *
 * `navigator.onLine` sagt lediglich, dass eine Netzwerkschnittstelle aktiv
 * ist. Auf einer Industrieanlage heißt das gar nichts: das Tablet hängt am
 * WLAN des Schaltschranks, der Browser meldet stolz "online", und der Server
 * ist trotzdem nicht zu sprechen. Der Flag ist deshalb nur ein HINWEIS.
 *
 * Maßgeblich ist, wie die letzten echten Anfragen ausgegangen sind: ein
 * Timeout oder ein Netzwerkfehler zählt hier so viel wie ein gezogenes Kabel.
 */
@Injectable({ providedIn: 'root' })
export class NetworkStatusService {

  /**
   * Der Browser-Flag. Nur ein Hinweis – `false` ist verlässlich (dann geht
   * sicher nichts), `true` bedeutet nichts.
   */
  private readonly browserOnline$ = new BehaviorSubject<boolean>(
    typeof navigator === 'undefined' ? true : navigator.onLine
  );

  /**
   * Was die letzten Anfragen gezeigt haben. Startet optimistisch: solange
   * nichts fehlgeschlagen ist, wird ein Versuch unternommen.
   */
  private readonly backendErreichbar$ = new BehaviorSubject<boolean>(true);

  /** Wann zuletzt ein Netzwerkfehler auftrat – Grundlage der Wiederanlauf-Abstände. */
  private letzterFehlerMs = 0;

  /** Wie oft hintereinander etwas fehlgeschlagen ist. Steuert den Abstand. */
  private fehlerInFolge = 0;

  constructor(private zone: NgZone) {
    if (typeof window !== 'undefined') {
      merge(
        fromEvent(window, 'online').pipe(map(() => true)),
        fromEvent(window, 'offline').pipe(map(() => false))
      ).subscribe(online => {
        this.zone.run(() => {
          this.browserOnline$.next(online);

          if (online) {
            /*
             * Der Browser meldet wieder Netz. Das ist noch kein Beweis, dass
             * das Backend antwortet – aber ein guter Grund, es erneut zu
             * versuchen. Die Sperre wird deshalb aufgehoben.
             */
            this.fehlerInFolge = 0;
            this.letzterFehlerMs = 0;
            this.backendErreichbar$.next(true);
          } else {
            this.backendErreichbar$.next(false);
          }
        });
      });
    }
  }

  /** Zustandsstrom für die Oberfläche. */
  get online$(): Observable<boolean> {
    return this.backendErreichbar$.asObservable();
  }

  get browserMeldetOnline(): boolean {
    return this.browserOnline$.value;
  }

  /**
   * Lohnt ein Versuch über das Netz?
   *
   * `false` nur, wenn der Browser sicher offline ist oder kurz zuvor eine
   * Anfrage im Netzwerkfehler endete. Nach einer Wartezeit wird wieder
   * probiert – wachsend, damit auf einer toten Leitung nicht im Sekundentakt
   * angeklopft wird.
   */
  lohntVersuch(): boolean {
    if (!this.browserMeldetOnline) {
      return false;
    }

    if (this.backendErreichbar$.value) {
      return true;
    }

    return Date.now() - this.letzterFehlerMs >= this.naechsterAbstandMs();
  }

  /** Eine Anfrage kam durch: die Leitung steht. */
  meldeErfolg(): void {
    this.fehlerInFolge = 0;
    this.letzterFehlerMs = 0;

    if (!this.backendErreichbar$.value) {
      this.zone.run(() => this.backendErreichbar$.next(true));
    }
  }

  /** Eine Anfrage endete im Netzwerkfehler oder Timeout. */
  meldeNetzwerkfehler(): void {
    this.letzterFehlerMs = Date.now();
    this.fehlerInFolge = Math.min(this.fehlerInFolge + 1, 10);

    if (this.backendErreichbar$.value) {
      this.zone.run(() => this.backendErreichbar$.next(false));
    }
  }

  /**
   * Abstand bis zum nächsten Versuch: 15 s, 30 s, 60 s, 120 s, dann 5 Minuten.
   *
   * Bewusst wachsend und nicht im Sekundentakt: ein enger Polling-Loop hält
   * das Funkmodul wach, frisst Akku und bringt auf einer toten Leitung nichts.
   */
  private naechsterAbstandMs(): number {
    const stufen = [15_000, 30_000, 60_000, 120_000, 300_000];

    return stufen[Math.min(this.fehlerInFolge, stufen.length - 1)];
  }

  /**
   * Ist das ein echter Netzwerkfehler – also ein Grund, offline
   * weiterzuarbeiten?
   *
   * Die Unterscheidung ist der Kern der ganzen Fehlerbehandlung:
   *
   * - Status 0 heißt "die Anfrage hat den Server nie erreicht": DNS,
   *   abgebrochene Verbindung, CORS, oder unser eigener Timeout.
   * - 408 und 504 sind Zeitüberschreitungen unterwegs.
   * - 502/503 heißt, dass der Server (oder der Proxy davor) gerade nicht
   *   antworten kann.
   *
   * Alles andere – 400, 401, 403, 404, 409, 500 – ist eine ECHTE Antwort.
   * Die darf niemals zum Offline-Fallback führen: eine fehlende Berechtigung
   * ist keine schlechte Verbindung, und sie lokal wegzuspeichern würde dem
   * Benutzer vorgaukeln, seine Änderung käme später schon durch.
   */
  static istNetzwerkfehler(fehler: unknown): boolean {
    if (fehler instanceof TimeoutFehler) {
      return true;
    }

    if (!(fehler instanceof HttpErrorResponse)) {
      return false;
    }

    return (
      fehler.status === 0
      || fehler.status === 408
      || fehler.status === 502
      || fehler.status === 503
      || fehler.status === 504
    );
  }

  /** Ist das ein Anmeldeproblem? Dann NICHT offline weiterarbeiten. */
  static istAuthFehler(fehler: unknown): boolean {
    return fehler instanceof HttpErrorResponse && fehler.status === 401;
  }
}

/**
 * Eigener Fehlertyp für unsere Zeitüberschreitung.
 *
 * `rxjs/timeout` wirft standardmäßig einen `TimeoutError`, den man nur am
 * Namen erkennt. Ein eigener Typ ist eindeutig und lässt sich mit `instanceof`
 * abfragen – wichtig, weil genau daran hängt, ob lokal weitergearbeitet wird.
 */
export class TimeoutFehler extends Error {
  constructor(public readonly nachMs: number) {
    super(`Die Anfrage hat länger als ${nachMs} ms gedauert.`);
    this.name = 'TimeoutFehler';
  }
}
